import React, { useMemo, useState } from 'react';
import { Button, Typography, Box, Grid, TextField, ToggleButton, ToggleButtonGroup, Dialog, DialogContent, Stack } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { addDays, format } from 'date-fns';
interface QuoteFormProps {
  /** Called after a successful submission (e.g. to auto-close a dialog). */
  onSuccess?: () => void;
  /** Hide the small heading/intro above the fields (used when a dialog title already exists). */
  hideHeading?: boolean;
}
interface DaySlot {
  key: string;
  /** Human label like "Tomorrow" or "Tue, Jun 24" */
  dayLabel: string;
}
/** Build the next `count` weekday (business) days starting from tomorrow. */
function getBusinessDays(count: number): DaySlot[] {
  const days: DaySlot[] = [];
  const today = new Date();
  let cursor = addDays(today, 1);
  let added = 0;
  while (added < count) {
    const dow = cursor.getDay(); // 0 = Sun, 6 = Sat
    const isWeekend = dow === 0 || dow === 6;
    if (!isWeekend) {
      const isTomorrow = format(cursor, 'yyyy-MM-dd') === format(addDays(today, 1), 'yyyy-MM-dd');
      days.push({
        key: format(cursor, 'yyyy-MM-dd'),
        dayLabel: isTomorrow ? 'Tomorrow' : format(cursor, 'EEE, MMM d')
      });
      added += 1;
    }
    cursor = addDays(cursor, 1);
  }
  return days;
}
const TIME_WINDOWS = [{
  value: 'morning',
  label: 'Morning'
}, {
  value: 'afternoon',
  label: 'Afternoon'
}] as const;
export function QuoteForm({
  onSuccess,
  hideHeading = false
}: QuoteFormProps) {
  const [status, setStatus] = useState<'idle' | 'submitting'>('idle');
  const [thankYouOpen, setThankYouOpen] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<string>('');
  // First 3 business days by default; 6 when "see more options" is expanded.
  const allDays = useMemo(() => getBusinessDays(6), []);
  const visibleDays = showMore ? allDays : allDays.slice(0, 3);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    setTimeout(() => {
      setStatus('idle');
      setThankYouOpen(true);
      onSuccess?.();
    }, 1200);
  };
  return <>
      <form onSubmit={handleSubmit} data-id="element-126">
        {!hideHeading && <>
            <Typography variant="h5" color="primary.main" fontWeight="bold" gutterBottom data-id="element-127">
              Tell Us About Your Project
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{
          mb: 4
        }} data-id="element-128">
              No commitment required. We respect your privacy.
            </Typography>
          </>}

        <Grid container spacing={3} data-id="element-129">
          <Grid item xs={12} sm={6} data-id="element-130">
            <TextField fullWidth label="First Name" required variant="outlined" data-id="element-131" />
          </Grid>
          <Grid item xs={12} sm={6} data-id="element-132">
            <TextField fullWidth label="Last Name" required variant="outlined" data-id="element-133" />
          </Grid>
          <Grid item xs={12} sm={6} data-id="element-134">
            <TextField fullWidth label="Phone Number" required variant="outlined" type="tel" data-id="element-135" />
          </Grid>
          <Grid item xs={12} sm={6} data-id="element-136">
            <TextField fullWidth label="Email Address" required variant="outlined" type="email" data-id="element-137" />
          </Grid>
          <Grid item xs={12} data-id="element-138">
            <TextField fullWidth label="Property Address" required variant="outlined" placeholder="Where would you like the onsite estimate?" data-id="element-139" />
          </Grid>

          {/* Appointment time selection */}
          <Grid item xs={12} data-id="element-140">
            <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1.5
          }} data-id="element-141">
              <EventAvailableIcon color="primary" fontSize="small" data-id="element-142" />
              <Typography variant="subtitle1" color="primary.main" fontWeight="bold" data-id="element-143">
                Pick a preferred estimate time
              </Typography>
            </Box>

            <Stack spacing={1.5} data-id="element-144">
              {visibleDays.map(day => <Box key={day.key} sx={{
              display: 'flex',
              flexDirection: {
                xs: 'column',
                sm: 'row'
              },
              alignItems: {
                xs: 'stretch',
                sm: 'center'
              },
              gap: {
                xs: 0.75,
                sm: 2
              }
            }} data-id="element-145">
                  <Typography variant="body2" fontWeight={700} color="text.primary" sx={{
                minWidth: {
                  sm: 120
                }
              }} data-id="element-146">
                    {day.dayLabel}
                  </Typography>
                  <ToggleButtonGroup exclusive value={selectedSlot} onChange={(_e, val) => {
                if (val !== null) setSelectedSlot(val);
              }} size="small" sx={{
                flexWrap: 'wrap'
              }} data-id="element-147">
                    {TIME_WINDOWS.map(tw => {
                  const slotKey = `${day.key}|${tw.value}`;
                  return <ToggleButton key={slotKey} value={slotKey} sx={{
                    px: 2.5,
                    textTransform: 'none',
                    '&.Mui-selected': {
                      bgcolor: 'secondary.main',
                      color: 'secondary.contrastText',
                      '&:hover': {
                        bgcolor: 'secondary.main'
                      }
                    }
                  }} data-id="element-148">
                          {tw.label}
                        </ToggleButton>;
                })}
                  </ToggleButtonGroup>
                </Box>)}
            </Stack>

            {!showMore && <Button onClick={() => setShowMore(true)} size="small" sx={{
            mt: 1.5,
            textTransform: 'none'
          }} data-id="element-149">
                See more options
              </Button>}
          </Grid>

          <Grid item xs={12} data-id="element-150">
            <TextField fullWidth label="Project Details (Optional)" multiline rows={3} variant="outlined" placeholder="Tell us what kind of patio cover you're looking for..." data-id="element-151" />
          </Grid>
          <Grid item xs={12} data-id="element-152">
            <Button type="submit" variant="contained" color="secondary" size="large" fullWidth disabled={status === 'submitting'} sx={{
            py: 2,
            fontSize: '1.1rem'
          }} data-id="element-153">
              {status === 'submitting' ? 'Sending...' : 'Request My Free Onsite Estimate'}
            </Button>
          </Grid>
        </Grid>
      </form>

      {/* Thank-you popup shown after submitting */}
      <Dialog open={thankYouOpen} onClose={() => setThankYouOpen(false)} maxWidth="xs" fullWidth aria-labelledby="thank-you-title" PaperProps={{
      sx: {
        borderRadius: 3
      }
    }} data-id="element-154">
        <DialogContent sx={{
        textAlign: 'center',
        py: 5,
        px: 4
      }} data-id="element-155">
          <CheckCircleIcon color="success" sx={{
          fontSize: 72,
          mb: 2
        }} data-id="element-156" />
          <Typography id="thank-you-title" variant="h4" color="primary.main" fontWeight="bold" gutterBottom data-id="element-157">
            Thank You!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{
          mb: 4
        }} data-id="element-158">
            Your free onsite estimate request has been received. A member of our
            team will reach out shortly to confirm your appointment time.
          </Typography>
          <Button variant="contained" color="secondary" onClick={() => setThankYouOpen(false)} sx={{
          px: 4
        }} data-id="element-159">
            Close
          </Button>
        </DialogContent>
      </Dialog>
    </>;
}